import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Stethoscope, Check, Plus, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { api } from '../utils/api';

const COMMON_CONDITIONS = [
  "Type 2 Diabetes",
  "Type 1 Diabetes",
  "Hypertension",
  "Heart Disease",
  "Coronary Artery Disease",
  "Atrial Fibrillation",
  "Heart Failure",
  "Obesity",
  "Prediabetes",
  "Chronic Kidney Disease",
  "COPD",
  "Asthma",
  "Sleep Apnea",
  "Hyperlipidemia",
  "Hypothyroidism",
  "Hyperthyroidism",
  "Anxiety",
  "Depression",
  "Osteoarthritis",
  "Rheumatoid Arthritis",
  "Chronic Pain",
  "Migraine",
  "GERD",
  "Irritable Bowel Syndrome",
  "Fibromyalgia"
];

const ConditionsModal = ({ open, onClose, patient, onSuccess }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (open && patient) {
      setSelectedConditions(patient.condition_tags || []);
      setSearchTerm('');
    }
  }, [open, patient]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredConditions = COMMON_CONDITIONS.filter(condition =>
    condition.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !selectedConditions.includes(condition)
  );

  const handleSelectCondition = (condition) => {
    if (!selectedConditions.includes(condition)) {
      setSelectedConditions([...selectedConditions, condition]);
    }
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleRemoveCondition = (condition) => {
    setSelectedConditions(selectedConditions.filter(c => c !== condition));
  };

  const handleAddCustomCondition = () => {
    const trimmed = searchTerm.trim();
    if (trimmed && !selectedConditions.includes(trimmed)) {
      setSelectedConditions([...selectedConditions, trimmed]);
      setSearchTerm('');
      setShowDropdown(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      e.preventDefault();
      handleAddCustomCondition();
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updatePatientConditions(patient.id, selectedConditions);
      toast.success('Conditions updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to update conditions');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="conditions-modal-title"
        data-testid="conditions-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' }}>
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 id="conditions-modal-title" className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Update Patient Conditions
              </h2>
              <p className="text-sm text-gray-500">
                {patient?.first_name} {patient?.last_name}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="rounded-full"
            aria-label="Close modal"
            data-testid="close-conditions-modal"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Selected Conditions Tags */}
          {selectedConditions.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Conditions ({selectedConditions.length})
              </label>
              <div className="flex flex-wrap gap-2" role="list" aria-label="Selected conditions">
                {selectedConditions.map((condition) => (
                  <span
                    key={condition}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200"
                    role="listitem"
                  >
                    <Tag className="w-3 h-3 mr-1.5" />
                    {condition}
                    <button
                      onClick={() => handleRemoveCondition(condition)}
                      className="ml-2 hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      aria-label={`Remove ${condition}`}
                      data-testid={`remove-condition-${condition.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Search/Add Input */}
          <div>
            <label htmlFor="condition-search" className="block text-sm font-medium text-gray-700 mb-2">
              Add Condition
            </label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="condition-search"
                  ref={inputRef}
                  type="text"
                  placeholder="Search or type custom condition..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  onKeyDown={handleKeyDown}
                  className="pl-10 pr-20"
                  aria-expanded={showDropdown}
                  aria-controls="conditions-dropdown"
                  aria-autocomplete="list"
                  data-testid="condition-search-input"
                />
                {searchTerm.trim() && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 px-2 text-purple-600 hover:text-purple-800"
                    onClick={handleAddCustomCondition}
                    data-testid="add-custom-condition-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                )}
              </div>

              {/* Dropdown */}
              {showDropdown && (searchTerm || filteredConditions.length > 0) && (
                <div 
                  id="conditions-dropdown"
                  ref={dropdownRef}
                  className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto"
                  role="listbox"
                  data-testid="conditions-dropdown"
                >
                  {filteredConditions.length > 0 ? (
                    filteredConditions.map((condition) => (
                      <button
                        key={condition}
                        className="w-full px-4 py-3 text-left hover:bg-purple-50 flex items-center justify-between transition-colors"
                        onClick={() => handleSelectCondition(condition)}
                        role="option"
                        aria-selected="false"
                        data-testid={`condition-option-${condition.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <span className="text-sm">{condition}</span>
                        <Check className="w-4 h-4 text-transparent" />
                      </button>
                    ))
                  ) : searchTerm.trim() ? (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      No matching conditions. Press Enter or click Add to create &ldquo;{searchTerm}&rdquo;
                    </div>
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      Start typing to search conditions
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Select from common conditions or type a custom one
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-full"
            data-testid="cancel-conditions-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full"
            style={{ background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' }}
            data-testid="save-conditions-btn"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConditionsModal;
